/* eslint-disable max-len */
import React, { memo, useContext, useEffect, useRef, useState } from 'react'

import { Icon, RotatingSpinner } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func } from 'prop-types'

import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

import EmptyState from './Components/EmptyState/EmptyState'
import LoadingModelCard from './Components/LoadingModelCard/LoadingModelCard'
import ModelCard from './Components/ModelCard/ModelCard'
import NewModelCard from './Components/NewModelCard/NewModelCard'
import SearchInput from './Components/SearchInput/SearchInput'
import { mockedModel } from './Constants/mock'
import useGetLibraryItems from './Hooks/useGetLibraryItems'

const size = { height: '32px', width: '32px' }

function LibraryContent({ onNavigate }) {
  const pageContext = useContext(ThreeDStudioPageContext)
  const isLoggedIn = pageContext?.userLoggedIn
  const pageConfigLoaded = pageContext?.pageConfigLoaded
  const extractingCoreStyles = pageContext?.extractingCoreStyles
  const isGenerating = useThreeDStudioStore(state => state.generation.isGenerating)
  const isEnhancing = useThreeDStudioStore(state => state.generation.isEnhancing)
  const enhancingSessionId = useThreeDStudioStore(state => state.generation.enhancingSessionId)
  const scrollContainerRef = useRef(null)
  const sentinelRef = useRef(null)
  const [keywords, setKeywords] = useState('')

  const {
    items,
    hasNextPage,
    handleLoadMore,
    isFetchingNextPage,
    isLoading,
    isError,
    meta,
  } = useGetLibraryItems(isLoggedIn, keywords)

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    const sentinel = sentinelRef.current
    if (!scrollContainer || !sentinel || !hasNextPage || isFetchingNextPage) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          handleLoadMore()
        }
      },
      { root: scrollContainer, rootMargin: '100px', threshold: 0 }
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, handleLoadMore])

  const remainingCount = Math.max(0, (meta?.totalCount ?? 0) - items.length)
  const loadingMoreSkeletonCount = Math.min(
    remainingCount,
    Number(meta?.perPage) || 10
  )
  const shouldShowSkeletonItems = !extractingCoreStyles
    && items.length > 0
    && hasNextPage
    && isFetchingNextPage
    && loadingMoreSkeletonCount > 0
  const authPending = !extractingCoreStyles && !pageConfigLoaded
  const shouldShowLoading = !extractingCoreStyles && !isError && (authPending || (isLoggedIn && isLoading))
  const shouldShowError = isError && !isLoading
  const shouldShowContent = extractingCoreStyles || (!authPending && !isLoading && (items.length > 0 || isGenerating))
  const shouldShowPlaceholder = !extractingCoreStyles
    && !authPending
    && !isLoading
    && !isError
    && !isGenerating
    && items.length === 0
  const shouldShowSearch = extractingCoreStyles || (meta?.totalCount ?? 0) >= 10 || keywords.length > 0

  return (
    <div className="library-content">
      {shouldShowSearch && <SearchInput onSearch={setKeywords} />}
      {shouldShowLoading && (
        <div className="library-content__loading">
          <Icon svgComponent={RotatingSpinner} size={size} />
        </div>
      )}
      {shouldShowError && (
        <div className="library-content__error">Failed to load!</div>
      )}
      {shouldShowPlaceholder && <EmptyState />}
      {shouldShowContent && (
        <div
          ref={scrollContainerRef}
          className="model-card-list"
        >
          {(!isGenerating || isEnhancing) && <NewModelCard onNavigate={onNavigate} />}
          {isGenerating && !isEnhancing && (
            <LoadingModelCard
              onNavigate={onNavigate}
              content="Generating..."
            />
          )}
          {!extractingCoreStyles && items.map(({ id, ...rest }) => (
            <ModelCard
              key={`model-card-${id}`}
              sessionId={id}
              isProcessing={isEnhancing && String(id) === String(enhancingSessionId)}
              onNavigate={onNavigate}
              {...rest}
            />
          ))}
          {extractingCoreStyles && (
            <>
              <ModelCard sessionId={mockedModel.id} attributes={mockedModel.attributes} onNavigate={onNavigate} />
              <ModelCard
                sessionId={mockedModel.id}
                attributes={mockedModel.attributes}
                isProcessing
                onNavigate={onNavigate}
              />
              <LoadingModelCard onNavigate={onNavigate} content="Generating..." />
              <div className="library-content__loading-more">
                <LoadingModelCard />
              </div>
            </>
          )}
          {shouldShowSkeletonItems && (
            <div className="library-content__loading-more">
              {Array.from({ length: loadingMoreSkeletonCount }, (_, i) => (
                <LoadingModelCard key={`loading-more-${i}`} />
              ))}
            </div>
          )}
          {items.length > 0 && hasNextPage && (
            <div
              ref={sentinelRef}
              className="library-content__sentinel"
              aria-hidden="true"
            />
          )}
        </div>
      )}
    </div>
  )
}

LibraryContent.propTypes = {
  onNavigate: func.isRequired,
}

export default memo(LibraryContent)
